// src/pages/PrivacyPage.tsx
// Drop this into your pages/ directory and add a /privacy route in App.tsx

export default function PrivacyPage() {
  const lastUpdated = 'October 2026'

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground text-sm mb-10">Last updated: {lastUpdated}</p>

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
          <section>
            <p>
              RoleRound ("we", "us", "our") is an AI-powered behavioral interview practice tool.
              This policy explains what data we collect, how we use it, and your rights over it. By
              using RoleRound, you agree to this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">1. What we collect</h2>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside">
              <li>
                <span className="text-foreground font-medium">Account information</span> — your
                email address and name when you sign up via email or Google OAuth.
              </li>
              <li>
                <span className="text-foreground font-medium">Interview responses</span> — the text
                you type during practice sessions, including answers to behavioral questions.
              </li>
              <li>
                <span className="text-foreground font-medium">Story-building conversations</span> —
                what you tell the story coach, and the draft stories you choose to save to your
                Story Bank. If you attach a resume to seed a conversation, it is used for that
                conversation only and is not stored. The conversation itself is not saved — only the
                stories you approve.
              </li>
              <li>
                <span className="text-foreground font-medium">Session data</span> — your scores,
                feedback reports, and competency progress across sessions.
              </li>
              <li>
                <span className="text-foreground font-medium">Usage data</span> — anonymized events
                (e.g. "session started", "session completed") used to improve the product. Your
                answers are never included in analytics.
              </li>
              <li>
                <span className="text-foreground font-medium">Payment information</span> — if you
                subscribe to Pro, Stripe processes your payment. We never see or store your card
                number.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">2. How we use your data</h2>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside">
              <li>To run your interview sessions and generate AI feedback.</li>
              <li>To save your session history and track your progress over time.</li>
              <li>To manage your subscription and process payments.</li>
              <li>
                To send transactional emails (e.g. account confirmation, session summaries) — no
                marketing email without your consent.
              </li>
              <li>To identify and fix bugs and improve product quality.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">3. AI processing</h2>
            <p className="text-muted-foreground">
              Your interview responses and story-building conversations (including a resume, if you
              attach one) are sent to <span className="text-foreground">Anthropic</span> (the maker
              of Claude) to generate feedback and draft stories. Anthropic processes this data under
              their{' '}
              <a
                href="https://www.anthropic.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2"
              >
                privacy policy
              </a>
              .{' '}
              <span className="text-foreground font-medium">
                We do not use your interview responses to train any AI model without your explicit
                consent.
              </span>{' '}
              AI feedback is provided as coaching guidance only — it is not a professional
              assessment and should not be treated as one.
            </p>
            <p className="text-muted-foreground mt-3">
              If you use the optional <span className="text-foreground">voice input</span> button to
              speak an answer, your browser transcribes the audio using its built-in
              speech-recognition service. In most browsers (including Chrome) this sends the audio
              to <span className="text-foreground">Google</span> for transcription. Voice input is
              off by default and only active while you hold a recording session — if you never use
              it, no audio is captured or sent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">4. Who we share data with</h2>
            <p className="text-muted-foreground mb-3">
              We use the following third-party services to operate RoleRound. We share only what
              each service needs to function:
            </p>
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Service</th>
                    <th className="text-left px-4 py-2 font-medium">Purpose</th>
                    <th className="text-left px-4 py-2 font-medium">What they receive</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-2 font-medium">Anthropic</td>
                    <td className="px-4 py-2 text-muted-foreground">AI interview engine</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      Your interview responses (no email or name)
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Supabase</td>
                    <td className="px-4 py-2 text-muted-foreground">Database & authentication</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      All account and session data (encrypted at rest)
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Stripe</td>
                    <td className="px-4 py-2 text-muted-foreground">Payment processing</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      Email, payment info (we never see card numbers)
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Google</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      Speech-to-text transcription
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">
                      Audio of spoken answers — only when you use voice input (no email or name)
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">PostHog</td>
                    <td className="px-4 py-2 text-muted-foreground">Product analytics</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      Anonymized usage events only — no answer text, no email
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Vercel</td>
                    <td className="px-4 py-2 text-muted-foreground">App hosting</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      Standard web request logs (IP, browser)
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-medium">Sentry</td>
                    <td className="px-4 py-2 text-muted-foreground">Error monitoring</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      Error details only — answer text is explicitly excluded
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground mt-3">We do not sell your data to anyone. Ever.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">5. Data retention</h2>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside">
              <li>
                Your account and session data is retained for as long as your account is active.
              </li>
              <li>
                If you delete your account, all your data — including interview responses, scores,
                and reports — is permanently deleted immediately. There is no grace period.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">6. Your rights (CCPA & general)</h2>
            <p className="text-muted-foreground mb-3">You have the right to:</p>
            <ul className="space-y-2 text-muted-foreground list-disc list-inside">
              <li>
                <span className="text-foreground font-medium">
                  Delete your account and all data
                </span>{' '}
                — available in Settings → Account → Delete Account. Takes effect immediately.
              </li>
              <li>
                <span className="text-foreground font-medium">Access your data</span> — email us at{' '}
                <a
                  href="mailto:support@roleround.com"
                  className="text-primary underline underline-offset-2"
                >
                  support@roleround.com
                </a>{' '}
                to request a copy.
              </li>
              <li>
                <span className="text-foreground font-medium">Opt out of analytics</span> — contact
                us and we will exclude your account from PostHog tracking.
              </li>
              <li>
                <span className="text-foreground font-medium">California residents</span> — under
                CCPA, you have the right to know what personal information we collect and to request
                deletion. We do not sell personal information.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">7. Security</h2>
            <p className="text-muted-foreground">
              Your data is encrypted at rest (AES-256) and in transit (TLS 1.3). Access to your data
              is restricted to your account only — no other user can see your sessions or responses.
              API keys and secrets are stored in server-side environments only and are never exposed
              to the browser.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">8. Cookies</h2>
            <p className="text-muted-foreground">
              We use cookies for authentication (to keep you logged in) and analytics (PostHog). No
              advertising cookies are used. You can clear cookies at any time via your browser
              settings, which will log you out of RoleRound.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">9. Children</h2>
            <p className="text-muted-foreground">
              RoleRound is not directed at children under 13. We do not knowingly collect data from
              anyone under 13. If you believe a child has created an account, contact us and we will
              delete it immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">10. Changes to this policy</h2>
            <p className="text-muted-foreground">
              We may update this policy as the product evolves. If we make material changes, we will
              notify you by email or by a notice in the app before the change takes effect.
              Continued use after notice constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-3">11. Contact</h2>
            <p className="text-muted-foreground">
              Questions about this policy? Email us at{' '}
              <a
                href="mailto:support@roleround.com"
                className="text-primary underline underline-offset-2"
              >
                support@roleround.com
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
